/* For shared functions across the widgets */
import React from 'react';
import { Link } from '../../spa/Components/Link/Link';
import sortBy from 'lodash/sortBy';
import isNil from 'lodash/isNil';
import cloneDeep from 'lodash/cloneDeep';
import i18n from '../../shared/utils/i18n';
import classNames from 'classnames';
import { getTeamLogoUrl } from '~shared/utils/sportsbook';
import {
    EMPTY_LENGTH_CHECK,
    HOME_SELECTION,
    DRAW_SELECTION,
    HOME_SELECTION_ORDER,
    DRAW_SELECTION_ORDER,
    AWAY_SELECTION_ORDER,
    TWO_WAY_MARKET_SELECTION_SCHEMA,
    THREE_WAY_MARKET_SELECTION_SCHEMA,
    YES_NO_MARKET_SELECTION_SCHEMA,
    OVER_UNDER_MARKET_SELECTION_SCHEMA,
    SPECIAL_HEADERS_SPORT_SLUG_LIST,
    SELECTION_I18N_KEY_LABEL_ONE,
    SELECTION_I18N_KEY_LABEL_TWO,
    SELECTION_I18N_KEY_LABEL_HOME,
    SELECTION_I18N_KEY_LABEL_DRAW,
    SELECTION_I18N_KEY_LABEL_AWAY,
    SELECTION_I18N_KEY_LABEL_DRAW_X,
    ALL_MARKETS_I18N_KEY,
    GTM_BET_CARD,
    AWAY_SELECTION,
    THREE_MARKET_HEADERS,
    YES_SELECTION,
    YES_SELECTION_ORDER,
    NO_SELECTION,
    NO_SELECTION_ORDER,
    OVER_SELECTION,
    OVER_SELECTION_ORDER,
    UNDER_SELECTION,
    UNDER_SELECTION_ORDER,
} from './constants';
import BetCard from '../components/react/BetCard';
import EventSchedule from '../components/react/EventSchedule';
import isToday from 'date-fns/is_today';
import Scoreboard from '../components/react/Scoreboard/Scoreboard';
import { seoUrlEvent, seoUrlInplayEvent } from '../../spa/Utils/SeoUrls';
import CountDownTimer from '../components/react/CountDownTimer';

/**
* Determines if a widget has content to be displayed
* @param {array} events - specific to the widget
* @param {Object} commonDataEvents - common events
* @return {Boolean} return true if we can show, otherwise false
*
*/
export function showWidget(events, commonDataEvents) {

    if (typeof events === 'undefined' || !events.length) {
        return false;
    }

    const commonEvents = Object.keys(commonDataEvents);

    const widget = events.filter((event) => {

        return commonEvents.includes(String(event.event_id));
    });

    return widget.length !== EMPTY_LENGTH_CHECK;
}

/**
 * Sort competitors into an ordered list
 * @param {Object} competitors The competitors object
 * @param {Boolean} reverse The competitors object
 * @return {Array} The ordered list of competitors
 */
export function sortCompetitors(competitors, reverse=false) {

    const sorted = sortBy(competitors, ['order']);

    return reverse ? sorted.reverse() : sorted;
}

/**
 * Sort selections into an ordered list
 * This legacy function relies on selection.type - it should not.
 * We should have already ordered selections using the selection ordering endpoint (See getSelectionOrder()).
 * However refactoring this has BIG QA implications, so we'll assume that if we have selection.order and it
 * is not null, then we can use it. Ensures backward compatability.
 * @param {Object} selections The selections object
 * @param {Object} reverse is an Us sport?
 * @return {Array} The ordered list of selections
 */
export function sortSelections(selections, reverse = false) {
    // I'm not proud of it, but I had to
    let orderedSelections = Object.keys(selections).map((keys) => {
        switch(selections[keys].type) {
            case HOME_SELECTION:
                selections[keys].order = HOME_SELECTION_ORDER;
                break;
            case DRAW_SELECTION:
                selections[keys].order = DRAW_SELECTION_ORDER;
                break;
            case AWAY_SELECTION_ORDER:
                selections[keys].order = AWAY_SELECTION_ORDER;
                break;
            case YES_SELECTION:
                selections[keys].order = YES_SELECTION_ORDER;
                break;
            case NO_SELECTION:
                selections[keys].order = NO_SELECTION_ORDER;
                break;
            case OVER_SELECTION:
                selections[keys].order = OVER_SELECTION_ORDER;
                break;
            case UNDER_SELECTION:
                selections[keys].order = UNDER_SELECTION_ORDER;
                break;
            default:
                break;
        }

            return selections[keys];
    });
    const sorted = sortBy(orderedSelections, ['order']);

    return reverse ? sorted.reverse() : sorted;
}

/**
 * Get the sport slug, market name, the length of selections and build the header array
 * @param {String} sportSlug Sport Slug index for "special headers"
 * @param {String} headerLabel Sport Slug index for "special headers"
 * @param {Object} market The market object
 * @param {Boolean} usesThreeMarketView if true 3 markets are assumetd
 * @param {Array} columns additional market columns
 * @return {Array} A dynamic header for passed markets
 */
export function getMarketHeader(sportSlug, headerLabel, market, usesThreeMarketView = false, columns = []) {
    let header = [headerLabel, ...columns];
    if (usesThreeMarketView) {
        const headers = THREE_MARKET_HEADERS[sportSlug] || [];

        return [...header, ...headers.map(header => i18n.t(`mkt_headers.${header}`))];
    }

    switch (market.selection_schema) {
        case TWO_WAY_MARKET_SELECTION_SCHEMA:
            if (SPECIAL_HEADERS_SPORT_SLUG_LIST.includes(sportSlug)) {
                // market.name 1 2
                header = [
                    ...header,
                    i18n.t(SELECTION_I18N_KEY_LABEL_ONE),
                    i18n.t(SELECTION_I18N_KEY_LABEL_TWO),
                ];
            } else {
                // market.name home away
                header = [
                    ...header,
                    i18n.t(SELECTION_I18N_KEY_LABEL_HOME),
                    i18n.t(SELECTION_I18N_KEY_LABEL_AWAY),
                ];
            }
            break;
        case THREE_WAY_MARKET_SELECTION_SCHEMA:
            if (SPECIAL_HEADERS_SPORT_SLUG_LIST.includes(sportSlug)) {
                // market.name 1 x 2
                header = [
                    ...header,
                    i18n.t(SELECTION_I18N_KEY_LABEL_ONE),
                    i18n.t(SELECTION_I18N_KEY_LABEL_DRAW_X),
                    i18n.t(SELECTION_I18N_KEY_LABEL_TWO),
                ];
            } else {
                // market.name home draw away
                header = [
                    ...header,
                    i18n.t(SELECTION_I18N_KEY_LABEL_HOME),
                    i18n.t(SELECTION_I18N_KEY_LABEL_DRAW),
                    i18n.t(SELECTION_I18N_KEY_LABEL_AWAY),
                ];
            }
            break;
        default:
            header = [ ...header ];
        break;
    }

    return header;
}

/**
 * Returns a custom competitor template.
 * @param {Object} competitor The object with competitor data
 * @param {Number} key For DOM Element key property
 * @param {Boolean} showAt Should Display character @
 * @param {Object} event event object
 * @returns {String} HTML tag
 */
export function createCompetitorTemplate(competitor, key, showAt, event) {
    const { name, slug: teamSlug } = competitor;

    const {
        tournament_logos: tournamentLogos,
        sport_slug: sportSlug,
        category_slug: categorySlug,
    } = event;

    const showTeamLogo = tournamentLogos
    ? tournamentLogos.showTeamLogo
    : false;

    let teamLogo = null;
    if (showTeamLogo) {
        const logoUrl = getTeamLogoUrl(sportSlug, categorySlug, teamSlug);

        teamLogo = <img
            src={logoUrl}
            className="featured-matches-widget__competitor-logo"
            />;
    }

    return (
        <div className="featured-matches-widget__competitor-wrapper" key={key}>
            {teamLogo}
            <span className={'featured-matches-widget__event-text featured-matches-widget__event-competitor'}>
                    {name} {!isNil(showAt) && showAt === key && '@'}
            </span>
        </div>
    );
}

/**
 * @param {object} selections the market selections - with missed selection(s)
 * @param {number} selection_schema market selection schema
 * @returns {object} selections object with missed selection based on selection schema
 */
export const addMissingSelections = (selections, selection_schema) => {
    const selectionsLength = Object.keys(selections).length;

    if (selection_schema === TWO_WAY_MARKET_SELECTION_SCHEMA || selection_schema === THREE_WAY_MARKET_SELECTION_SCHEMA) {
        const initialSelectionTypes = selection_schema === THREE_WAY_MARKET_SELECTION_SCHEMA
            ? [
                HOME_SELECTION,
                DRAW_SELECTION,
                AWAY_SELECTION,
            ]
            : [
                HOME_SELECTION,
                AWAY_SELECTION,
            ];
        const missing_types = initialSelectionTypes.reduce((acc_value, current_type) => {
            const exists = Object.values(selections).filter((current_selection) => {

                return current_selection.type === current_type;
            }).length;
            if (!exists) {
                acc_value.push(current_type);
            }

            return acc_value;
        }, []);

        const new_selections = cloneDeep(selections);
        for (let i = 0; i < missing_types.length; i++) {
            const missing_type = missing_types[i];
            new_selections[missing_type] = {
                type: missing_type,
            };
        }

        return new_selections;
    }

    // adding missing selection for Yes/No or Over/Under
    if (selectionsLength < 2
        && [YES_NO_MARKET_SELECTION_SCHEMA, OVER_UNDER_MARKET_SELECTION_SCHEMA].includes(selection_schema)
    ) {
        // WS update can contain 0 | 1 selection - UI needs 2 to display betcard
        let newSelections = {
            dummyA: {},
            dummyB: {},
        };

        if (selectionsLength === 0) {

            return newSelections;
        }

        const selectionTypePairs = {
            [YES_SELECTION]: NO_SELECTION,
            [NO_SELECTION]: YES_SELECTION,
            [OVER_SELECTION]: UNDER_SELECTION,
            [UNDER_SELECTION]: OVER_SELECTION,
        };

        const [ nonDummySelectionId ] = Object.keys(selections);
        // Type is required for selection sorting
        const { type: nonDummySelectionType } = selections[nonDummySelectionId];
        const matchingSelectionType = selectionTypePairs[nonDummySelectionType];

        newSelections = {
            [nonDummySelectionId]: selections[nonDummySelectionId],
            dummy: { type: matchingSelectionType },
        };

        return newSelections;
    }

    return selections;
};

/**
 * Create a BetCard element with the specified event data
 * @param {Object} event The event object
 * @param {Number} marketId For BetCard marketId property
 * @param {Number} key For BetCard key property
 * @param {Function} descriptionTemplate Create the description template
 * @param {String} gtmAttribute For GTM attribute
 * @param {Boolean} reverse reverse selection order?
 * @return {BetCard} The BetCard element
 */
export function createBetCard(event, marketId, key, descriptionTemplate, gtmAttribute) {
    const betable = event.betable && event.markets[marketId].betable;
    const { isUsFormat } = event;
    // the betcard market
    const markets = { [marketId]: event.markets[marketId] };

    let _selections = null;
    const selection_schema = event.markets[marketId].selection_schema;

    switch (selection_schema) {
        case TWO_WAY_MARKET_SELECTION_SCHEMA:
        case THREE_WAY_MARKET_SELECTION_SCHEMA:
            _selections = addMissingSelections(event.markets[marketId].selections, selection_schema);
        break;
        default:
            _selections = event.markets[marketId].selections;
    }

    return (
        <BetCard
            key={key}
            selections={sortSelections(_selections, isUsFormat)}
            markets={markets}
            description={descriptionTemplate}
            betable={betable}
            isLive={Boolean(event.is_inplay)}
            gtmAttribute={gtmAttribute}
            showSelectionLabels={event.showSelectionLabels}>
        </BetCard>
    );
}

/**
 * if at least one market is betable with betable selections isSelectionsBetable will be true.
 * @param {Object} markets The market object
 * @returns {Boolean} is market betable?
 */
export function isSelectionsBetable(markets) {
    return Object.values(markets).some((market) => {
        const {
            betable: isMarketBetable = true,
            default_line_selections: defaultSelections = [],
            selections: marketSelections = {},
        } = market;

        // true if at least one selection in the market is betable.
        const isSelectionBetable = (
            defaultSelections
            .map(selectionId => marketSelections[selectionId] || {betable: false})
            .some((selection) => selection?.betable)
        ) || (
            Object.values(marketSelections).some((selection) => selection?.betable)
        );

        // if market betable and at least one selection is betable.
        return isMarketBetable && isSelectionBetable;
    });
}

/**
 * Create a BetCard element that displays three elements
 * @param {Object} event The event object
 * @param {Array<Number>} marketIds Array of market ids
 * @param {Number} key For BetCard key property
 * @param {Function} descriptionTemplate Create the description template
 * @param {String} gtmAttribute For GTM attribute
 * @return {BetCard} The BetCard element
 */
export function createThreeMarketBetCard(event, marketIds, key, descriptionTemplate, gtmAttribute) {
    const { markets, isUsFormat, is_inplay, betable: isEventBetable } = event;
    const betable = isEventBetable && isSelectionsBetable(markets);

    return (
        <BetCard
            key={key}
            selections={[]}
            description={descriptionTemplate}
            betable={betable}
            isLive={Boolean(is_inplay)}
            gtmAttribute={gtmAttribute}
            markets={markets}
            marketOrder={marketIds}
            usesThreeMarketView={true}
            isUsFormat={isUsFormat}
        />
    );
}

/**
 * Returns a custom header template.
 * @param {Object} event Event object
 * @param {Boolean} showEventSchedule Set the template to show EventSchedule Component
 * @param {String} gtmAttribute For GTM attribute
 * @param {boolean} hasBoostedOddsSelections Whether the event has boosted odds selections
 * @param {boolean} isSearch If the function is called from search page
 * @returns {String} HTML tag
 */
export function createDescriptionTemplate(event, showEventSchedule = false, gtmAttribute, hasBoostedOddsSelections = false, isSearch = false) {
    const betCardGTM = isSearch ? gtmAttribute : `${gtmAttribute}${GTM_BET_CARD}`;
    let eventUrl;
    const {
        isUsFormat,
        usesThreeMarketView,
        sport_slug: sportSlug,
        inplay: isInplay = false,
        is_streaming_available: isStreamingAvailable = false,
    } = event;

    if (isInplay) {
        eventUrl = seoUrlInplayEvent(event.event_url, event.id);
    } else {
        eventUrl = seoUrlEvent(event.event_url, event.id);
    }

    const eventLinkClass = classNames('featured-matches-widget__event-link', {
        'featured-matches-widget__event-link--three_market_view': usesThreeMarketView,
    });

    const isPreplayStreaming = !isInplay && isStreamingAvailable;

    return (
        <div className='featured-matches-widget__wrapper'>
            <div className='featured-matches-widget__schedule-column'>
                {(isToday(event.start_time) || showEventSchedule) ? (
                    <EventSchedule
                        startTime={event.start_time}
                        isInplay={isInplay} />
                ) : (
                    <CountDownTimer scheduledTime={event.start_time} />
                )}
            </div>
            <div className={`${'featured-matches-widget__market-column'}${usesThreeMarketView ? ' featured-matches-widget__market-column--three_market_view' : ''}`}>
                <Link className={eventLinkClass} to={eventUrl} gtmAttribute={betCardGTM}>
                    {sortCompetitors(event.competitors, isUsFormat).map((competitor, key) => createCompetitorTemplate(competitor, key, isUsFormat ? 0 : null, event))}
                    <span className='featured-matches-widget__event-text featured-matches-widget__event-markets-label'>
                        <span className='featured-matches-widget__event-markets-label-text'>
                            {i18n.t(ALL_MARKETS_I18N_KEY)}
                        </span>
                        <span className='featured-matches-widget__event-markets-arrow'/>
                        {
                            // The streaming icon is shown as part of the scoreboard if the event is inplay otherwise it's shown here.
                            isPreplayStreaming || hasBoostedOddsSelections
                            ? <span className='featured-matches-widget__icons'>
                                {hasBoostedOddsSelections && <span className='featured-matches-widget__icons-boosted-odds'/> }
                                {isPreplayStreaming && <span className='featured-matches-widget__icons-streaming'/> }
                            </span>
                            : null
                        }
                    </span>
                </Link>
            </div>
            {
                isInplay
                ? <Scoreboard
                        className='featured-matches-widget__scoreboard-column'
                        sport={sportSlug}
                        event={event}
                        shouldShowStreaming={isStreamingAvailable}
                />
                : null
            }
        </div>
    );
}
